import React, { useState } from 'react';
import apiService from '../../services/api';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES, MODAL_WIDTHS } from '../../constants/popupStyles';

interface ApproveMaccabiModalProps {
    isOpen: boolean;
    onClose: () => void;
    task?: any;
    token: string;
    onSuccess?: () => void;
    onStatusMessage?: (message: { type: 'success' | 'error' | 'loading'; text: string } | null) => void;
}

export const ApproveMaccabiModal: React.FC<ApproveMaccabiModalProps> = ({
    isOpen,
    onClose,
    task,
    token,
    onSuccess,
    onStatusMessage,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Handle approve
    const handleApprove = async () => {
        if (!task || !token) return;

        setIsSubmitting(true);
        onStatusMessage?.({ type: 'loading', text: 'Approving Maccabi patient...' });

        try {
            const response = await apiService.updateObligation({
                token,
                patient_id: task.id,
                obligation_intake: 'approve',
                obligation_followup: 'approve',
                intake_obligation_verified: true,
                followup_obligation_verified: true,
                not_maccabi: false,
            });

            console.log('[ApproveMaccabiModal] Approve response:', response);
            if (response.op === 'delete') {
                console.log('[ApproveMaccabiModal] Task will be removed from list');
            }

            onStatusMessage?.({ type: 'success', text: 'Maccabi patient approved successfully' });

            // Auto-clear success message after 3 seconds
            setTimeout(() => {
                onStatusMessage?.(null);
            }, 3000);

            // Always refresh the task list to update or remove the task
            onSuccess?.();

            handleClose();
        } catch (error: any) {
            console.error('Failed to approve Maccabi patient:', error);
            onStatusMessage?.({
                type: 'error',
                text: error.message || 'Failed to approve Maccabi patient'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle not maccabi
    const handleNotMaccabi = async () => {
        if (!task || !token) return;

        setIsSubmitting(true);
        onStatusMessage?.({ type: 'loading', text: 'Marking as Not Maccabi...' });

        try {
            const response = await apiService.updateObligation({
                token,
                patient_id: task.id,
                obligation_intake: '',
                obligation_followup: '',
                intake_obligation_verified: false,
                followup_obligation_verified: false,
                not_maccabi: true,
            });

            console.log('[ApproveMaccabiModal] Not Maccabi response:', response);
            if (response.op === 'delete') {
                console.log('[ApproveMaccabiModal] Task will be removed from list');
            }

            onStatusMessage?.({ type: 'success', text: 'Marked as Not Maccabi successfully' });

            // Auto-clear success message after 3 seconds
            setTimeout(() => {
                onStatusMessage?.(null);
            }, 3000);

            // Always refresh the task list to update or remove the task
            onSuccess?.();

            handleClose();
        } catch (error: any) {
            console.error('Failed to mark as Not Maccabi:', error);
            onStatusMessage?.({
                type: 'error',
                text: error.message || 'Failed to mark as Not Maccabi'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle close
    const handleClose = () => {
        onClose();
    };

    return (
        <ModalWrapper modalId="ApproveMaccabiModal"
            isOpen={isOpen}
            onClose={handleClose}
            title="Approve Maccabi Patient"
            isLoading={isSubmitting}
            width={MODAL_WIDTHS.wide}
        >
            <div className="bg-[#EBF5F5] rounded px-4 py-4 space-y-3 mb-6">
                {task && (
                    <>
                        <div className="flex items-start gap-2">
                            <span className="font-semibold text-[#366A75] min-w-[120px]">Patient Name:</span>
                            <span className="text-[#366A75]">{task.first_name} {task.last_name}</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <span className="font-semibold text-[#366A75] min-w-[120px]">HM ID:</span>
                            <span className="text-[#366A75]">{task.HM_ID || '-'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <span className="font-semibold text-[#366A75] min-w-[120px]">Patient ID:</span>
                            <span className="text-[#366A75]">{task.patient_id || '-'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <span className="font-semibold text-[#366A75] min-w-[120px]">Issue:</span>
                            <span className="text-[#366A75]">{task.issue || '-'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                            <span className="font-semibold text-[#366A75] min-w-[120px]">Notes:</span>
                            <span className="text-[#366A75]">{task.task || 'Empty'}</span>
                        </div>
                    </>
                )}
            </div>

            <div className="flex items-center justify-center gap-4">
                <button
                    onClick={handleApprove}
                    disabled={isSubmitting}
                    className={POPUP_STYLES.button}
                >
                    Approve
                </button>
                <button
                    onClick={handleNotMaccabi}
                    disabled={isSubmitting}
                    className={POPUP_STYLES.button}
                >
                    Not Maccabi
                </button>
                <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};

